import type { CSSProperties } from 'react';
import type { GameReviewFrame, StoneColor } from '@wx-weiqi/contracts';
import { formatDuration } from '../domain/clock-projection';
import { reviewTimingRatio, type ReviewTimingSummary } from '../domain/game-review-presentation';
import { reviewTimingChartScale } from '../domain/review-timing-chart';
import { formatPreciseDuration } from '../domain/timing-report';

export function ReviewPlayerStone({ color }: { readonly color: StoneColor }) {
  return (
    <img
      className="review-player-stone"
      src={`/assets/teacher-lecture/${color === 'BLACK' ? 'black-slate' : 'white-shell'}.png`}
      alt={color === 'BLACK' ? '黑棋' : '白棋'}
    />
  );
}

export function RecentReviewTimingChart({
  frames,
  moveNumber,
}: {
  readonly frames: readonly GameReviewFrame[];
  readonly moveNumber: number;
}) {
  const timed = frames.filter((frame) => frame.elapsedMs !== null && frame.color !== null);
  const { ceilingMs, ticks } = reviewTimingChartScale(timed.map((frame) => frame.elapsedMs!));
  if (timed.length === 0) return <p className="review-timing-empty">当前进度暂无逐手用时记录。</p>;
  return (
    <div className="review-timing-chart" aria-label="最近逐手用时柱状图">
      <div className="review-chart-grid" aria-hidden="true">
        {ticks.map((tick) => (
          <div key={tick.valueMs} style={{ bottom: `${tick.percent}%` }}>
            <span>{tick.label}</span>
          </div>
        ))}
      </div>
      <ol className="review-chart-columns">
        {timed.map((frame) => (
          <li
            key={frame.moveNumber}
            className={`${frame.color === 'WHITE' ? 'is-white' : 'is-black'}${frame.moveNumber === moveNumber ? ' is-current' : ''}`}
            aria-current={frame.moveNumber === moveNumber ? 'step' : undefined}
            aria-label={`${frame.color === 'BLACK' ? '黑棋' : '白棋'}，第${frame.moveNumber}手，${formatPreciseDuration(frame.elapsedMs)}`}
            style={{ '--bar-height': `${(frame.elapsedMs! / ceilingMs) * 100}%` } as CSSProperties}
          >
            <div className="review-chart-plot">
              <strong className="review-chart-value">
                {formatPreciseDuration(frame.elapsedMs)}
              </strong>
              <span className="review-chart-bar" aria-hidden="true" />
            </div>
            <div className="review-chart-caption" aria-hidden="true">
              <span>{frame.color === 'BLACK' ? '黑' : '白'}</span>
              <span>第 {frame.moveNumber} 手</span>
              {frame.moveNumber === moveNumber ? <em>当前</em> : null}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function ReviewTiming({
  frame,
  moveNumber,
  visibleTiming,
  fullTiming,
  recentMoves,
  reportPending,
  reportDisabled,
  onReport,
  emptyTotalsAsZero = false,
}: {
  readonly frame: GameReviewFrame | undefined;
  readonly moveNumber: number;
  readonly visibleTiming: ReviewTimingSummary;
  readonly fullTiming: ReviewTimingSummary;
  readonly recentMoves: readonly GameReviewFrame[];
  readonly reportPending: boolean;
  readonly reportDisabled: boolean;
  readonly onReport?: () => void;
  readonly emptyTotalsAsZero?: boolean;
}) {
  const ratios = reviewTimingRatio(visibleTiming.black.totalMs, visibleTiming.white.totalMs);
  return (
    <div className="teacher-review-timing">
      <section className="review-current-time" aria-label="本手用时">
        <div>
          <h2>本手用时</h2>
          <span>
            {frame?.color === 'BLACK' ? '黑棋' : frame?.color === 'WHITE' ? '白棋' : '开局'} · 第{' '}
            {moveNumber} 手
          </span>
        </div>
        <strong>{formatPreciseDuration(visibleTiming.currentMoveMs)}</strong>
      </section>
      <section className="review-time-comparison" aria-label="用时对比">
        <header>
          <h2>用时对比</h2>
          <span>截至第 {moveNumber} 手</span>
        </header>
        {(['BLACK', 'WHITE'] as const).map((color, index) => {
          const summary = color === 'BLACK' ? visibleTiming.black : visibleTiming.white;
          return (
            <div className={`review-time-row ${color === 'WHITE' ? 'is-white' : ''}`} key={color}>
              <span className="review-time-side">
                <ReviewPlayerStone color={color} />
                {color === 'BLACK' ? '黑棋累计' : '白棋累计'}
              </span>
              <strong>
                {summary.moveCount === 0 && !emptyTotalsAsZero
                  ? '—'
                  : formatDuration(summary.totalMs)}
              </strong>
              <span className="review-time-average">
                平均{' '}
                {summary.moveCount === 0 && !emptyTotalsAsZero
                  ? '—'
                  : formatPreciseDuration(summary.averageMs)}
              </span>
              <span className="review-comparison-track" aria-hidden="true">
                <i style={{ width: `${ratios[index]}%` }} />
              </span>
            </div>
          );
        })}
        <p className="review-full-time">
          全局总用时 · 黑{' '}
          {fullTiming.black.moveCount === 0 ? '—' : formatDuration(fullTiming.black.totalMs)} / 白{' '}
          {fullTiming.white.moveCount === 0 ? '—' : formatDuration(fullTiming.white.totalMs)}
        </p>
      </section>
      <section className="review-recent-time" aria-label="最近逐手用时">
        <header>
          <h2>最近逐手用时</h2>
          {onReport !== undefined ? (
            <button type="button" disabled={reportDisabled || reportPending} onClick={onReport}>
              {reportPending ? '加载中…' : '详细用时报告'}
            </button>
          ) : null}
        </header>
        {emptyTotalsAsZero && recentMoves.length === 0 ? (
          <p className="review-timing-empty">前进棋谱后显示逐手用时。</p>
        ) : (
          <RecentReviewTimingChart frames={recentMoves} moveNumber={moveNumber} />
        )}
      </section>
    </div>
  );
}
